import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

// Client-side column sorting for tables (DESIGN.md › Tables › Sortable headers).
// Pair with `SortableTableHead`:
//   const { sorted, sortKey, sortDir, toggleSort, directionFor } = useTableSort(rows, "name");
//   <SortableTableHead direction={directionFor("name")} onSort={() => toggleSort("name")}>
// Clicking the active column flips direction; activating a new column starts
// ascending for strings and descending for numbers (biggest first).
export function useTableSort<T extends object>(
  items: T[],
  defaultKey: keyof T,
  defaultDir: SortDirection = "asc",
) {
  // Null until the user picks a column, so the default keeps applying while the
  // caller's columns are still loading — a `defaultKey` computed from async data
  // (QL-115: the clients table's configured columns) would otherwise be missed,
  // since useState only reads its initial value on the first render.
  const [chosen, setChosen] = useState<{ key: keyof T; dir: SortDirection } | null>(null);
  const sortKey = chosen?.key ?? defaultKey;
  const sortDir = chosen?.dir ?? defaultDir;

  const toggleSort = (key: keyof T) => {
    if (key === sortKey) {
      setChosen({ key, dir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      const sample = items.find((item) => item[key] != null)?.[key];
      setChosen({ key, dir: typeof sample === "number" ? "desc" : "asc" });
    }
  };

  const directionFor = (key: keyof T): SortDirection | null => (key === sortKey ? sortDir : null);

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        // A missing value compares as "", so it ranks below every number and string.
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort, directionFor };
}
